import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AlertLevel, PaidSource } from '@/types';
import { Analytics } from '@/utils/analytics';
import { ApolloError } from '@apollo/client';
import { BankDepositsList } from '@/components/payments/bank-deposits-list';
import { Button } from '@/components/button';
import { CollectedPaymentsList } from '@/components/payments/collected-payments-list';
import { createStyles } from '@/contexts/theme-context';
import { DepositNumberActionSheet } from '@/components/payments/deposit-number-action-sheet';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { formatCurrency } from '@/utils/money';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import { PillButton } from '@usepepper/ui/atoms/pill-button';
import { StyleProp, View, ViewStyle } from 'react-native';
import { Type } from '@/components/toast';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDepositManualPayments } from '@/hooks/payments/use-deposit-manual-payments';
import { useGetCollectedManualPayments } from '@/hooks/payments/use-get-collected-manual-payments';
import { useGetManualDeposits } from '@/hooks/payments/use-get-manual-deposits';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useToasts } from '@/contexts/toast-context';

export enum BankDepositsTab {
  Deposited = 'DEPOSITED',
  Undeposited = 'UNDEPOSITED',
}

interface Props {
  footerStyle?: StyleProp<ViewStyle>;
}

export const BankDepositsContent: React.FC<Props> = ({ footerStyle }) => {
  const [styles, theme] = useStyles();
  const { currencyCode } = usePaymentsContext();
  const { showErrorToast, showToast } = useToasts();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    user,
  } = useAuthenticatedUserContext();

  const [selectedTab, setSelectedTab] = useState(BankDepositsTab.Undeposited);
  const [isEnteringDepositNumber, setIsEnteringDepositNumber] = useState(false);

  const isUndepositedTab = selectedTab === BankDepositsTab.Undeposited;

  const {
    data: collectedData,
    error: collectedError,
    loading: collectedLoading,
    refetch: refetchCollected,
  } = useGetCollectedManualPayments({
    employeeUUID: user.uuid,
    onError: () => showErrorToast(),
    supplierUUID,
  });
  const {
    data: depositsData,
    error: depositsError,
    loading: depositsLoading,
    refetch: refetchDeposits,
  } = useGetManualDeposits({
    employeeUUID: user.uuid,
    onError: () => showErrorToast(),
    skip: isUndepositedTab,
    supplierUUID,
  });

  const [depositManualPayments, { loading: isSubmitting }] =
    useDepositManualPayments();

  const collectedPayments = useMemo(
    () => collectedData?.getCollectedManualPayments.payments ?? [],
    [collectedData],
  );
  const deposits = depositsData?.getManualDeposits.deposits ?? [];

  const { totalCashInMicros, totalChecksInMicros } = useMemo(
    () =>
      collectedPayments.reduce(
        (totals, payment) => ({
          totalCashInMicros:
            totals.totalCashInMicros +
            (payment.paid_source === PaidSource.Cash
              ? payment.total_amount_in_micros
              : 0),
          totalChecksInMicros:
            totals.totalChecksInMicros +
            (payment.paid_source === PaidSource.ManualCheck
              ? payment.total_amount_in_micros
              : 0),
        }),
        { totalCashInMicros: 0, totalChecksInMicros: 0 },
      ),
    [collectedPayments],
  );

  const onConfirmDeposit = useCallback(
    async (depositNumber: string) => {
      try {
        const response = await depositManualPayments({
          variables: {
            depositNumber,
            employeeUUID: user.uuid,
            paymentBatches: collectedPayments.map((payment) => ({
              payment_batch_id: payment.payment_batch_id,
            })),
            supplierUUID,
          },
        });

        const result = response.data?.depositManualPayments;

        if (result?.alert_level !== AlertLevel.Positive) {
          showErrorToast(result?.alert_message ?? undefined);
          return;
        }

        setIsEnteringDepositNumber(false);
        showToast({
          content: i18n.t('bank_deposits.deposit_recorded'),
          type: Type.POSITIVE,
        });
        await Promise.all([refetchCollected(), refetchDeposits()]);
      } catch (e) {
        if (e instanceof ApolloError && getMessageFromGQLError(e)) {
          showErrorToast(getMessageFromGQLError(e));
        } else {
          showErrorToast();
        }
      }
    },
    [
      collectedPayments,
      depositManualPayments,
      refetchCollected,
      refetchDeposits,
      showErrorToast,
      showToast,
      supplierUUID,
      user.uuid,
    ],
  );

  const loading = isUndepositedTab ? collectedLoading : depositsLoading;
  const error = isUndepositedTab ? collectedError : depositsError;
  const hasCollectedPayments = collectedPayments.length > 0;

  return (
    <>
      <View style={styles.pillRow}>
        <PillButton
          button={{
            active: isUndepositedTab,
            label: i18n.t('bank_deposits.undeposited'),
            onPress: () => {
              Analytics.track('bank_deposits.undeposited_tab.press');
              setSelectedTab(BankDepositsTab.Undeposited);
            },
          }}
          variant="medium"
        />
        <PillButton
          button={{
            active: !isUndepositedTab,
            label: i18n.t('bank_deposits.deposited'),
            onPress: () => {
              Analytics.track('bank_deposits.deposited_tab.press');
              setSelectedTab(BankDepositsTab.Deposited);
            },
          }}
          variant="medium"
        />
      </View>

      <View style={styles.content}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator />
          </View>
        ) : error ? (
          <EmptyStateMinimal
            containerStyle={styles.emptyStateContainer}
            text={i18n.t('bank_deposits.load_error_message')}
          />
        ) : isUndepositedTab ? (
          hasCollectedPayments ? (
            <CollectedPaymentsList
              currencyCode={currencyCode}
              payments={collectedPayments}
            />
          ) : (
            <EmptyStateMinimal
              containerStyle={styles.emptyStateContainer}
              text={i18n.t('bank_deposits.undeposited_empty_message')}
            />
          )
        ) : deposits.length ? (
          <BankDepositsList currencyCode={currencyCode} deposits={deposits} />
        ) : (
          <EmptyStateMinimal
            containerStyle={styles.emptyStateContainer}
            text={i18n.t('bank_deposits.deposited_empty_message')}
          />
        )}
      </View>

      {isUndepositedTab ? (
        <View style={[styles.footer, footerStyle]}>
          <View style={styles.totalRow}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-base"
            >
              {i18n.t('bank_deposits.total_cash')}
            </Typography>
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {formatCurrency(totalCashInMicros, currencyCode)}
            </Typography>
          </View>
          <View style={styles.totalRow}>
            <Typography
              color={theme.sk.color.text.secondary}
              variant="paragraph-base"
            >
              {i18n.t('bank_deposits.total_checks')}
            </Typography>
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-base"
            >
              {formatCurrency(totalChecksInMicros, currencyCode)}
            </Typography>
          </View>
          <Button
            accessibilityLabel={i18n.t('bank_deposits.mark_as_deposited')}
            disabled={!hasCollectedPayments}
            onPress={() => {
              Analytics.track('bank_deposits.mark_as_deposited.press');
              setIsEnteringDepositNumber(true);
            }}
            style={styles.markAsDepositedButton}
            trackingData={{ name: 'bank_deposits.mark_as_deposited_button' }}
          >
            {i18n.t('bank_deposits.mark_as_deposited')}
          </Button>
        </View>
      ) : null}

      {isEnteringDepositNumber ? (
        <DepositNumberActionSheet
          isSubmitting={isSubmitting}
          onClose={() => setIsEnteringDepositNumber(false)}
          onConfirm={onConfirmDeposit}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    flex: 1,
  },
  emptyStateContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  footer: {
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    gap: theme.sk.spacing.spaceXsmall,
    paddingBottom: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceSMedium,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  markAsDepositedButton: {
    marginTop: theme.sk.spacing.spaceSmall,
  },
  pillRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));
